import { useState, useEffect } from 'react';
import { csoDashboardAPI } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Leaf } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function CSODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    csoDashboardAPI.getData().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 bg-muted rounded animate-pulse" /><div className="h-72 bg-muted rounded-xl animate-pulse" /></div>;

  return (
    <div data-testid="cso-dashboard-page" className="space-y-6">
      <h1 className="cp-page-title flex items-center gap-2"><Leaf className="w-6 h-6 text-primary" /> CSO Dashboard</h1>

      <div className="cp-chart-card animate-fade-in" data-testid="cso-emission-trends">
        <h3 className="text-sm font-heading font-semibold mb-4">Emission Trends (CCF & PCF)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.emission_trends || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="ccf" name="CCF" stroke="#166534" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="pcf_total" name="PCF Total" stroke="#4F46E5" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="cp-chart-card animate-fade-in" data-testid="cso-highest-emissions">
        <h3 className="text-sm font-heading font-semibold mb-4">Highest Emission Products</h3>
        <div className="cp-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead className="text-right">PCF (tCO2/t)</TableHead>
                <TableHead className="text-right">Production (t)</TableHead>
                <TableHead className="text-right">Total Emissions (tCO2)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.highest_emission_products?.map((p, i) => (
                <TableRow key={p.id || i}>
                  <TableCell className="font-mono text-sm">{i + 1}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><span className="cp-badge-indigo">{p.sector}</span></TableCell>
                  <TableCell className="text-right font-mono">{p.pcf}</TableCell>
                  <TableCell className="text-right font-mono">{p.monthly_production?.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{(p.pcf * p.monthly_production).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
